import React, { useState, useEffect, Fragment, useRef, useMemo} from 'react';
import { Nav, Tab, Col,Row,Table,Card,Button, Modal, Form, InputGroup, FormCheck, Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import axios from 'axios';
import "../../../assets/custom/scss/quizScreen.scss"
import { useLanguage } from '../../../i18n/LanguageContext';
import QuizResultModal from './QuizResultModal';
import { getSavedKeywordHint, isQuestionKeywordHintDisabled } from './questionKeywordHighlight';
import apiUrl from '../../../config/api';

const isAnswerValueFilled = (answer) => (
  Array.isArray(answer)
    ? answer.length > 0
    : answer !== null && answer !== undefined && answer !== ''
);

const QuizScreen = ({breakpoint, subject, quizParams, questions, setShowQuizScreen, reviewAttempt = null, initialAttempt = null }) => {
  const { t } = useLanguage();
  const text = t.subjectsPage;
  const activeQuestions = useMemo(
    () => questions.slice(0, Number(quizParams.numberOfQuestions || questions.length)),
    [questions, quizParams.numberOfQuestions]
  );
  const isSavedReview = Boolean(reviewAttempt);

  const [currentQuestion, setCurrentQuestion] = useState(() => {
    if (initialAttempt?.currentQuestionIndex === null || initialAttempt?.currentQuestionIndex === undefined) {
      return 0;
    }

    return Math.min(
      Math.max(0, Number(initialAttempt.currentQuestionIndex) || 0),
      Math.max(0, activeQuestions.length - 1)
    );
  });
  const [selectedAnswers, setSelectedAnswers] = useState(() => (
    reviewAttempt?.selectedAnswers || initialAttempt?.selectedAnswers || Array(activeQuestions.length).fill(null)
  ));
  const [timeRemaining, setTimeRemaining] = useState(() => (
    initialAttempt?.timeRemainingSeconds ?? quizParams.timer * 60
  )); // Timer in secondi
  const [fullscreen, setFullscreen] = useState(true); // Modale
  const [isFullscreen, setIsFullscreen] = useState(false); // Browser
  const [viewedQuestionIds, setViewedQuestionIds] = useState(() => {
    const viewedIds = activeQuestions
      .filter((question) => question.wasViewed)
      .map((question) => question._id);

    return new Set(viewedIds.length ? viewedIds : activeQuestions[0]?._id ? [activeQuestions[0]._id] : []);
  });
  const [startedAt] = useState(() => (
    reviewAttempt?.startedAt
      ? new Date(reviewAttempt.startedAt)
      : initialAttempt?.startedAt
        ? new Date(initialAttempt.startedAt)
        : new Date()
  ));
  const [attemptId, setAttemptId] = useState(initialAttempt?._id || null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPassage, setSelectedPassage] = useState(null);
  const [completionSummary, setCompletionSummary] = useState(reviewAttempt?.completionSummary || null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showEndQuizConfirm, setShowEndQuizConfirm] = useState(false);
  const [showMobileQuestionNavigator, setShowMobileQuestionNavigator] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(isSavedReview);
  const [keywordHintsByQuestionId, setKeywordHintsByQuestionId] = useState(() => (
    activeQuestions.reduce((hints, question) => ({
      ...hints,
      [question._id]: getSavedKeywordHint(question),
    }), {})
  ));
  const [keywordDisabledByQuestionId, setKeywordDisabledByQuestionId] = useState(() => (
    activeQuestions.reduce((disabledHints, question) => ({
      ...disabledHints,
      [question._id]: isQuestionKeywordHintDisabled(question),
    }), {})
  ));
  const [keywordDraft, setKeywordDraft] = useState('');
  const [isSavingKeyword, setIsSavingKeyword] = useState(false);
  const [keywordMessage, setKeywordMessage] = useState('');
  const [showKeywordPanel, setShowKeywordPanel] = useState(false);
  const [showKeywordHighlights, setShowKeywordHighlights] = useState(true);
  const hasStartedAttemptRequestRef = useRef(Boolean(initialAttempt?._id || reviewAttempt));
  const quizArchiveName = subject.archiveName || '';
  const getQuestionSubjectName = (question) => (
    question?.subjectName || (subject.isArchiveQuiz ? '' : subject.name)
  );
  const getQuestionArchiveName = (question) => (
    question?.archiveName || quizArchiveName
  );
  const currentQuestionArchiveName = getQuestionArchiveName(activeQuestions[currentQuestion]) || (!getQuestionSubjectName(activeQuestions[currentQuestion]) ? subject.name : '');
  const currentQuestionSubjectName = getQuestionSubjectName(activeQuestions[currentQuestion]);
  const currentQuestionSubjectSubtitle = currentQuestionSubjectName && currentQuestionSubjectName !== currentQuestionArchiveName
    ? currentQuestionSubjectName
    : '';
  const currentQuestionData = activeQuestions[currentQuestion];
  const isKeywordHelperEnabled = quizParams.showKeywords !== false
    && String(currentQuestionData?.questionType || '').toLowerCase() === 'closed';
  const isAutomaticOpenAnswer = quizParams.autoShowOpenAnswers === true
    && String(currentQuestionData?.questionType || '').toLowerCase() === 'open'
    && !isReviewMode;
  const stopWords = new Set([
    'a', 'ad', 'al', 'allo', 'ai', 'che', 'con', 'del', 'della', 'delle', 'degli', 'dei', 'dal', 'dallo', 'alla', 'alle', 'agli', 'nel', 'nella',
    'nelle', 'negli', 'per', 'tra', 'fra', 'una', 'uno', 'gli', 'le', 'la', 'il', 'lo', 'un', 'in', 'di', 'da',
    'dalle', 'dagli', 'dai', 'dale', 'dall', 'sul', 'sulla', 'sulle', 'sugli', 'piu', 'suoi', 'sue', 'suo',
    'e', 'ed', 'i', 'o', 'and', 'the', 'for', 'with', 'from', 'into', 'that', 'this', 'are', 'is', 'not', 'all', 'none',
    'come', 'cosa', 'quale', 'quali', 'quando', 'quanto', 'quanti', 'dove', 'puo', 'puoi', 'pu', 'sono', 'sia',
    'essere', 'viene', 'vengono', 'anche', 'solo', 'sola', 'sole', 'sua', 'sui', 'cui', 'piu', 'meno', 'molto',
    'molti', 'molte', 'ogni', 'tutti', 'tutte', 'altro', 'altra', 'altri', 'altre', 'questo', 'questa', 'questi',
    'queste', 'quello', 'quella', 'quelli', 'quelle',
  ]);

  const genericKeywordWords = new Set([
    'sistema', 'sistemi', 'progettazione', 'progetto', 'oggetto', 'oggetti', 'risposta', 'risposte', 'domanda',
    'domande', 'utente', 'utenti', 'interazione', 'interazioni', 'funzione', 'funzioni', 'funzionalita', 'elemento',
    'elementi', 'processo', 'processi', 'modello', 'modelli', 'metodo', 'metodi', 'caso', 'casi', 'livello',
    'livelli', 'informazione', 'informazioni', 'dati', 'dato', 'modo', 'modalita', 'parte', 'parti',
    'prodotto', 'prodotti', 'prestazione', 'prestazioni',
  ]);

  const normalizeKeywordText = (value = '') => value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const tokenizeKeywordText = (value = '') => normalizeKeywordText(value)
    .split(' ')
    .filter((word) => word.length > 2 && !stopWords.has(word));

  const tokenizeKeywordTextLoose = (value = '') => normalizeKeywordText(value)
    .split(' ')
    .filter((word) => word.length > 1);

  const getKeywordSimilarity = (firstWords, secondWords) => {
    const firstSet = new Set(firstWords);
    const secondSet = new Set(secondWords);
    const intersectionCount = [...firstSet].filter((word) => secondSet.has(word)).length;
    const unionCount = new Set([...firstSet, ...secondSet]).size;

    return unionCount ? intersectionCount / unionCount : 0;
  };

  const getCommonPrefixScore = (firstWords, secondWords) => {
    const comparableLength = Math.min(firstWords.length, secondWords.length);
    let prefixLength = 0;

    while (prefixLength < comparableLength && firstWords[prefixLength] === secondWords[prefixLength]) {
      prefixLength += 1;
    }

    return firstWords.length ? prefixLength / firstWords.length : 0;
  };

  const getLongestCommonSequenceScore = (firstWords, secondWords) => {
    let longestSequence = 0;

    firstWords.forEach((firstWord, firstIndex) => {
      secondWords.forEach((secondWord, secondIndex) => {
        if (firstWord !== secondWord) {
          return;
        }

        let sequenceLength = 0;

        while (
          firstWords[firstIndex + sequenceLength]
          && firstWords[firstIndex + sequenceLength] === secondWords[secondIndex + sequenceLength]
        ) {
          sequenceLength += 1;
        }

        longestSequence = Math.max(longestSequence, sequenceLength);
      });
    });

    return firstWords.length ? longestSequence / firstWords.length : 0;
  };

  const hasKeywordNegation = (value = '') => /\b(non|nessuna|nessuno|none|not|no)\b/.test(normalizeKeywordText(value));

  const getOrderedUniqueWords = (words = []) => [...new Set(words)];

  const getKeywordLine = (label, words, limit = 4) => {
    const uniqueWords = getOrderedUniqueWords(words).slice(0, limit);

    if (!uniqueWords.length) {
      return '';
    }

    return `${label}: ${uniqueWords.join(', ')}`;
  };

  const getKeywordLineValue = (line = '') => line.replace(/^[^:]+:\s*/, '').trim();

  const hasKeywordLineValue = (value = '') => getKeywordLineValue(value).length > 0;

  const sanitizeKeywordHint = (value = '') => value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => {
      const normalizedLine = line.toLowerCase();
      const isContextLine = normalizedLine.startsWith(`${text.keywordContext.toLowerCase()}:`);
      return !isContextLine || hasKeywordLineValue(line);
    })
    .join('\n')
    .trim();

  const getOriginalKeywordWords = (value = '') => (
    value.match(/[\p{L}\p{N}]+/gu)?.map((word) => ({
      raw: word,
      normalized: normalizeKeywordText(word),
    })) || []
  );

  const getNormalizedKeywordSequence = (value = '') => (
    getOriginalKeywordWords(value)
      .map((word) => word.normalized)
      .filter(Boolean)
  );

  const isConsecutiveKeywordSequence = (needleWords = [], haystackWords = []) => {
    if (!needleWords.length || needleWords.length > haystackWords.length) {
      return false;
    }

    for (let startIndex = 0; startIndex <= haystackWords.length - needleWords.length; startIndex += 1) {
      const matchesSequence = needleWords.every((word, wordIndex) => (
        haystackWords[startIndex + wordIndex] === word
      ));

      if (matchesSequence) {
        return true;
      }
    }

    return false;
  };

  const getDisplayKeywordTerm = (originalText, normalizedTerm, { includePrecedingVerb = false } = {}) => {
    const targetWords = tokenizeKeywordTextLoose(normalizedTerm);
    const originalWords = getOriginalKeywordWords(originalText);

    if (!targetWords.length || !originalWords.length) {
      return normalizedTerm;
    }

    for (let startIndex = 0; startIndex < originalWords.length; startIndex += 1) {
      if (originalWords[startIndex].normalized !== targetWords[0]) {
        continue;
      }

      const matchedIndexes = [startIndex];
      let searchIndex = startIndex + 1;
      let targetIndex = 1;

      while (targetIndex < targetWords.length && searchIndex < originalWords.length) {
        const originalWord = originalWords[searchIndex];

        if (originalWord.normalized === targetWords[targetIndex]) {
          matchedIndexes.push(searchIndex);
          targetIndex += 1;
        } else if (!stopWords.has(originalWord.normalized)) {
          break;
        }

        searchIndex += 1;
      }

      if (targetIndex !== targetWords.length) {
        continue;
      }

      let displayStartIndex = matchedIndexes[0];
      const displayEndIndex = matchedIndexes[matchedIndexes.length - 1];

      if (
        includePrecedingVerb
        && displayStartIndex > 0
        && ['e', 'è', 'é'].includes(originalWords[displayStartIndex - 1].raw.toLowerCase())
      ) {
        displayStartIndex -= 1;
      }

      return originalWords
        .slice(displayStartIndex, displayEndIndex + 1)
        .map((word) => word.raw)
        .join(' ');
    }

    return normalizedTerm;
  };

  const phraseExistsInText = (textValue, normalizedPhrase) => {
    const textWords = getOriginalKeywordWords(textValue).map((word) => word.normalized);
    const phraseWords = getOriginalKeywordWords(normalizedPhrase).map((word) => word.normalized);

    if (!phraseWords.length || phraseWords.length > textWords.length) {
      return false;
    }

    for (let startIndex = 0; startIndex <= textWords.length - phraseWords.length; startIndex += 1) {
      const matchesPhrase = phraseWords.every((word, wordIndex) => textWords[startIndex + wordIndex] === word);

      if (matchesPhrase) {
        return true;
      }
    }

    return false;
  };

  const getDistinctivePhrases = (correctText, wrongTexts) => {
    const correctWords = getOriginalKeywordWords(correctText);
    const correctNormalizedWords = correctWords.map((word) => word.normalized);
    const wrongNormalizedSequences = wrongTexts.map(getNormalizedKeywordSequence);
    const phrases = [];

    for (let phraseLength = 1; phraseLength <= Math.min(5, correctWords.length); phraseLength += 1) {
      for (let startIndex = 0; startIndex <= correctWords.length - phraseLength; startIndex += 1) {
        const phraseWords = correctWords.slice(startIndex, startIndex + phraseLength);
        const normalizedPhraseWords = correctNormalizedWords.slice(startIndex, startIndex + phraseLength);
        const meaningfulWords = phraseWords.filter((word) => !stopWords.has(word.normalized) && word.normalized.length > 2);

        if (!meaningfulWords.length) {
          continue;
        }

        const startsWithStopWord = stopWords.has(normalizedPhraseWords[0]);
        const endsWithStopWord = stopWords.has(normalizedPhraseWords[normalizedPhraseWords.length - 1]);

        if (phraseLength > 1 && (startsWithStopWord || endsWithStopWord)) {
          continue;
        }

        if (
          phraseLength === 1
          && wrongNormalizedSequences.some((wrongWords) => wrongWords.includes(normalizedPhraseWords[0]))
        ) {
          continue;
        }

        const appearsInWrongAnswer = wrongNormalizedSequences.some((wrongWords) => (
          isConsecutiveKeywordSequence(normalizedPhraseWords, wrongWords)
        ));

        if (!appearsInWrongAnswer) {
          phrases.push(phraseWords.map((word) => word.raw).join(' '));
        }
      }

      if (phrases.length) {
        return getOrderedUniqueWords(phrases).slice(0, 2);
      }
    }

    return [];
  };

  const getDifferencePhrases = (correctText, competitorText) => {
    const correctOriginalWords = getOriginalKeywordWords(correctText);
    const competitorWords = getNormalizedKeywordSequence(competitorText);
    const correctWords = correctOriginalWords.map((word) => word.normalized);
    const candidatePhrases = [];
    let firstDifferenceIndex = 0;

    while (
      firstDifferenceIndex < correctWords.length
      && firstDifferenceIndex < competitorWords.length
      && correctWords[firstDifferenceIndex] === competitorWords[firstDifferenceIndex]
    ) {
      firstDifferenceIndex += 1;
    }

    if (!correctWords.length || firstDifferenceIndex >= correctWords.length) {
      return [];
    }

    for (let phraseLength = 1; phraseLength <= Math.min(5, correctWords.length); phraseLength += 1) {
      for (let startIndex = 0; startIndex <= correctWords.length - phraseLength; startIndex += 1) {
        const endIndex = startIndex + phraseLength - 1;
        const touchesDivergence = startIndex <= firstDifferenceIndex && endIndex >= firstDifferenceIndex;
        const startsBeforeDivergence = firstDifferenceIndex > 0 && startIndex === firstDifferenceIndex - 1;

        if (!touchesDivergence && !startsBeforeDivergence) {
          continue;
        }

        const phraseWords = correctWords.slice(startIndex, startIndex + phraseLength);
        const phraseOriginalWords = correctOriginalWords.slice(startIndex, startIndex + phraseLength);
        const existsInCompetitor = isConsecutiveKeywordSequence(phraseWords, competitorWords);

        if (existsInCompetitor) {
          continue;
        }

        const meaningfulWords = phraseWords.filter((word) => !stopWords.has(word) && word.length > 2);

        if (!meaningfulWords.length) {
          continue;
        }

        const startsWithStopWord = stopWords.has(phraseWords[0]);
        const endsWithStopWord = stopWords.has(phraseWords[phraseWords.length - 1]);
        const isIncompletePhrase = phraseLength > 1 && (startsWithStopWord || endsWithStopWord);
        const allWordsExistInCompetitor = phraseWords.every((word) => competitorWords.includes(word));

        if (isIncompletePhrase) {
          continue;
        }

        if (phraseLength === 1 && allWordsExistInCompetitor) {
          continue;
        }

        const semanticQuality = phraseLength >= 2 ? 1.5 : 0.35;
        const proximityToFirstDifference = 1 / (Math.abs(startIndex - firstDifferenceIndex) + 1);
        const sequenceBreakBonus = allWordsExistInCompetitor ? 1.4 : 0;
        const redundancyPenalty = Math.max(0, phraseLength - Math.max(2, meaningfulWords.length)) * 0.35;
        const lengthPenalty = phraseLength * 0.18;
        const score = semanticQuality
          + proximityToFirstDifference
          + sequenceBreakBonus
          + (meaningfulWords.length * 0.35)
          - lengthPenalty
          - redundancyPenalty;

        candidatePhrases.push({
          phrase: phraseOriginalWords.map((word) => word.raw).join(' '),
          length: phraseLength,
          score,
        });
      }

      const sufficientCandidates = candidatePhrases
        .filter((candidate) => candidate.length === phraseLength)
        .sort((firstCandidate, secondCandidate) => secondCandidate.score - firstCandidate.score);

      if (sufficientCandidates.length) {
        return [sufficientCandidates[0].phrase];
      }
    }

    return [];
  };

  const getSpecialCorrectAnswerHint = (correctText) => {
    const normalizedCorrectText = normalizeKeywordText(correctText);

    if (/\bnessuna\b/.test(normalizedCorrectText)) {
      return getDisplayKeywordTerm(correctText, 'nessuna');
    }

    if (/\bnone\b/.test(normalizedCorrectText)) {
      return getDisplayKeywordTerm(correctText, 'none');
    }

    return '';
  };

  const cleanKeywordCandidate = (value = '') => String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s,.;:!?'"“”‘’`«»()[\]{}]+|[\s,.;:!?'"“”‘’`«»()[\]{}]+$/g, '')
    .trim();

  const getKeywordCandidateWords = (value = '') => getOriginalKeywordWords(value)
    .filter((word) => word.normalized.length > 2 && !stopWords.has(word.normalized));

  const isWeakSingleKeyword = (phrase = '') => {
    const words = getKeywordCandidateWords(phrase);

    return words.length === 1 && genericKeywordWords.has(words[0].normalized);
  };

  const getKeywordTermQuality = (term = '') => {
    const words = getOriginalKeywordWords(term);
    const meaningfulWords = words.filter((word) => (
      word.normalized.length > 2 && !stopWords.has(word.normalized)
    ));

    if (!words.length || !meaningfulWords.length) {
      return -10;
    }

    const startsWithStopWord = stopWords.has(words[0].normalized);
    const endsWithStopWord = stopWords.has(words[words.length - 1].normalized);
    const genericPenalty = meaningfulWords.filter((word) => genericKeywordWords.has(word.normalized)).length * 0.75;

    return (meaningfulWords.length * 1.2)
      + Math.min(term.length / 30, 1)
      - (startsWithStopWord ? 1.5 : 0)
      - (endsWithStopWord ? 2.5 : 0)
      - genericPenalty;
  };

  const doKeywordTermsOverlap = (firstTerm = '', secondTerm = '') => {
    const firstWords = getOriginalKeywordWords(firstTerm)
      .map((word) => word.normalized)
      .filter((word) => word.length > 2 && !stopWords.has(word));
    const secondWordSet = new Set(
      getOriginalKeywordWords(secondTerm)
        .map((word) => word.normalized)
        .filter((word) => word.length > 2 && !stopWords.has(word))
    );

    return firstWords.some((word) => secondWordSet.has(word));
  };

  const dedupeOverlappingKeywordTerms = (terms = []) => {
    const selectedTerms = [];

    terms
      .map((term) => ({ term, quality: getKeywordTermQuality(term) }))
      .sort((firstTerm, secondTerm) => {
        if (secondTerm.quality !== firstTerm.quality) {
          return secondTerm.quality - firstTerm.quality;
        }

        return secondTerm.term.length - firstTerm.term.length;
      })
      .forEach(({ term }) => {
        if (!selectedTerms.some((selectedTerm) => doKeywordTermsOverlap(selectedTerm, term))) {
          selectedTerms.push(term);
        }
      });

    return selectedTerms;
  };

  const getWrongPhrasePresenceCount = (phrase = '', wrongTexts = []) => {
    const normalizedPhrase = getNormalizedKeywordSequence(phrase).join(' ');

    if (!normalizedPhrase) {
      return 0;
    }

    return wrongTexts.filter((wrongText) => phraseExistsInText(wrongText, normalizedPhrase)).length;
  };

  const addKeywordCandidate = (candidates, phrase, source, baseScore = 0) => {
    const cleanPhrase = cleanKeywordCandidate(phrase);
    const words = getKeywordCandidateWords(cleanPhrase);
    const allWords = getOriginalKeywordWords(cleanPhrase);

    if (!cleanPhrase || !words.length) {
      return;
    }

    if (allWords.length > 7 || cleanPhrase.length > 80 || isWeakSingleKeyword(cleanPhrase)) {
      return;
    }

    candidates.push({
      phrase: cleanPhrase,
      source,
      baseScore,
      wordCount: allWords.length,
      meaningfulWordCount: words.length,
    });
  };

  const getVisualKeywordCandidates = (correctText = '') => {
    const candidates = [];
    const quotePatterns = [
      /"([^"]{2,80})"/gu,
      /“([^”]{2,80})”/gu,
      /«([^»]{2,80})»/gu,
    ];

    quotePatterns.forEach((pattern) => {
      [...correctText.matchAll(pattern)].forEach((match) => addKeywordCandidate(candidates, match[1], 'quoted', 8));
    });

    [...correctText.matchAll(/(?:\(([^)]{2,80})\)|\[([^\]]{2,80})\]|\{([^}]{2,80})\})/gu)]
      .forEach((match) => addKeywordCandidate(candidates, match[1] || match[2] || match[3], 'bracketed', 7));

    [...correctText.matchAll(/\b(?:[A-ZÀ-Þ]{2,}(?:[-/][A-ZÀ-Þ0-9]{2,})*|\d+(?:[.,]\d+)?%?(?:[-/]\d+(?:[.,]\d+)?%?)?)\b/g)]
      .forEach((match) => addKeywordCandidate(candidates, match[0], 'visual', 6));

    return candidates;
  };

  const getShortDistinctiveKeywordCandidates = (correctText = '', wrongTexts = []) => {
    const originalWords = getOriginalKeywordWords(correctText);
    const candidates = [];

    for (let phraseLength = 1; phraseLength <= Math.min(4, originalWords.length); phraseLength += 1) {
      for (let startIndex = 0; startIndex <= originalWords.length - phraseLength; startIndex += 1) {
        const phraseWords = originalWords.slice(startIndex, startIndex + phraseLength);
        const normalizedWords = phraseWords.map((word) => word.normalized);
        const meaningfulWords = phraseWords.filter((word) => (
          word.normalized.length > 2 && !stopWords.has(word.normalized)
        ));

        if (!meaningfulWords.length) {
          continue;
        }

        if (phraseLength > 1 && (stopWords.has(normalizedWords[0]) || stopWords.has(normalizedWords[normalizedWords.length - 1]))) {
          continue;
        }

        if (phraseLength === 1 && genericKeywordWords.has(normalizedWords[0])) {
          continue;
        }

        const phrase = phraseWords.map((word) => word.raw).join(' ');

        if (getWrongPhrasePresenceCount(phrase, wrongTexts) > 0) {
          continue;
        }

        const genericPenalty = meaningfulWords.filter((word) => genericKeywordWords.has(word.normalized)).length * 0.7;
        const lengthScore = phraseLength === 1
          ? 1.2
          : phraseLength === 2
            ? 2.8
            : phraseLength === 3
              ? 2.4
              : 1.7;
        const visualBonus = /[A-ZÀ-Þ]{2,}|\d|["“”«»()[\]]/.test(phrase) ? 1.4 : 0;
        const score = lengthScore
          + (meaningfulWords.length * 0.45)
          + visualBonus
          - genericPenalty
          - Math.max(0, phrase.length - 42) / 28;

        addKeywordCandidate(candidates, phrase, 'distinctive', score);
      }
    }

    return candidates;
  };

  const getBestKeywordCandidates = (correctText = '', wrongTexts = [], limit = 2, mode = 'all') => {
    const candidates = [
      ...(mode !== 'distinctive' ? getVisualKeywordCandidates(correctText) : []),
      ...(mode !== 'visual' ? getShortDistinctiveKeywordCandidates(correctText, wrongTexts) : []),
    ]
      .filter((candidate) => (
        phraseExistsInText(correctText, candidate.phrase)
        && getWrongPhrasePresenceCount(candidate.phrase, wrongTexts) === 0
      ));
    const uniqueCandidates = [];
    const seenCandidates = new Set();

    candidates
      .map((candidate) => {
        const normalizedPhrase = getNormalizedKeywordSequence(candidate.phrase).join(' ');
        const sourceBonus = candidate.source === 'quoted'
          ? 4
          : candidate.source === 'bracketed'
            ? 3.5
            : candidate.source === 'visual'
              ? 3
              : 0;
        const shortPhraseBonus = candidate.wordCount <= 3 ? 1.2 : 0;
        const score = candidate.baseScore
          + sourceBonus
          + shortPhraseBonus
          + (candidate.meaningfulWordCount * 0.35)
          + (getKeywordTermQuality(candidate.phrase) * 0.5)
          - Math.max(0, candidate.wordCount - 3) * 0.4;

        return { ...candidate, normalizedPhrase, score };
      })
      .sort((firstCandidate, secondCandidate) => {
        if (secondCandidate.score !== firstCandidate.score) {
          return secondCandidate.score - firstCandidate.score;
        }

        return firstCandidate.wordCount - secondCandidate.wordCount;
      })
      .forEach((candidate) => {
        if (
          !candidate.normalizedPhrase
          || seenCandidates.has(candidate.normalizedPhrase)
          || uniqueCandidates.some((selectedCandidate) => doKeywordTermsOverlap(selectedCandidate.phrase, candidate.phrase))
        ) {
          return;
        }

        seenCandidates.add(candidate.normalizedPhrase);
        uniqueCandidates.push(candidate);
      });

    return uniqueCandidates.slice(0, limit);
  };

  const getMostSimilarWrongAnswer = (correctText = '', wrongAnswers = []) => {
    const correctWords = tokenizeKeywordText(correctText);

    return wrongAnswers
      .map((answer) => {
        const wrongWords = tokenizeKeywordText(answer.text);
        const differencePhrases = getDifferencePhrases(correctText, answer.text);
        const longestDifferencePhraseLength = differencePhrases.reduce((longestLength, phrase) => (
          Math.max(longestLength, tokenizeKeywordTextLoose(phrase).length)
        ), 0);
        const similarity = getKeywordSimilarity(correctWords, wrongWords)
          + (getCommonPrefixScore(correctWords, wrongWords) * 0.35)
          + (getLongestCommonSequenceScore(correctWords, wrongWords) * 0.3)
          + (Math.min(longestDifferencePhraseLength / 3, 1) * 0.8)
          - (hasKeywordNegation(correctText) === hasKeywordNegation(answer.text) ? 0 : 0.2);

        return {
          text: answer.text,
          differencePhrases,
          longestDifferencePhraseLength,
          similarity: Math.max(0, similarity),
        };
      })
      .filter((match) => match.similarity > 0)
      .sort((firstMatch, secondMatch) => {
        if (firstMatch.longestDifferencePhraseLength !== secondMatch.longestDifferencePhraseLength) {
          return secondMatch.longestDifferencePhraseLength - firstMatch.longestDifferencePhraseLength;
        }

        return secondMatch.similarity - firstMatch.similarity;
      })[0];
  };

  const isReadableKeywordTerm = (term = '') => {
    const words = getOriginalKeywordWords(term);

    return term.length <= 80 && words.length <= 8;
  };

  const getKeywordCombinationCandidates = (correctText = '', wrongTexts = [], limit = 4) => {
    const candidates = wrongTexts
      .map((wrongText) => (
        getDifferencePhrases(correctText, wrongText)
          .map((phrase) => cleanKeywordCandidate(getDisplayKeywordTerm(correctText, phrase)))
          .filter((phrase) => (
            phrase
            && isReadableKeywordTerm(phrase)
            && !isWeakSingleKeyword(phrase)
            && phraseExistsInText(correctText, phrase)
            && !phraseExistsInText(wrongText, phrase)
          ))
          .sort((firstPhrase, secondPhrase) => (
            getOriginalKeywordWords(firstPhrase).length - getOriginalKeywordWords(secondPhrase).length
          ))[0]
      ))
      .filter(Boolean);
    const uniqueCandidates = getOrderedUniqueWords(candidates);

    if (uniqueCandidates.length < 2) {
      return [];
    }

    const selectedCandidates = [];
    const coveredWrongIndexes = new Set();

    while (coveredWrongIndexes.size < wrongTexts.length && selectedCandidates.length < limit) {
      const nextCandidate = uniqueCandidates
        .filter((candidate) => !selectedCandidates.includes(candidate))
        .map((candidate) => {
          const newlyCoveredIndexes = wrongTexts
            .map((wrongText, wrongIndex) => (
              !coveredWrongIndexes.has(wrongIndex) && !phraseExistsInText(wrongText, candidate)
                ? wrongIndex
                : null
            ))
            .filter((wrongIndex) => wrongIndex !== null);

          return {
            phrase: candidate,
            newlyCoveredIndexes,
            wordCount: getOriginalKeywordWords(candidate).length,
          };
        })
        .filter((candidate) => candidate.newlyCoveredIndexes.length)
        .sort((firstCandidate, secondCandidate) => {
          if (secondCandidate.newlyCoveredIndexes.length !== firstCandidate.newlyCoveredIndexes.length) {
            return secondCandidate.newlyCoveredIndexes.length - firstCandidate.newlyCoveredIndexes.length;
          }

          return firstCandidate.wordCount - secondCandidate.wordCount;
        })[0];

      if (!nextCandidate) {
        break;
      }

      selectedCandidates.push(nextCandidate.phrase);
      nextCandidate.newlyCoveredIndexes.forEach((wrongIndex) => coveredWrongIndexes.add(wrongIndex));
    }

    const correctContainsAllCandidates = selectedCandidates.every((candidate) => (
      phraseExistsInText(correctText, candidate)
    ));
    const wrongAnswerContainsAllCandidates = wrongTexts.some((wrongText) => (
      selectedCandidates.every((candidate) => phraseExistsInText(wrongText, candidate))
    ));
    const correctWords = getNormalizedKeywordSequence(correctText);
    const getCandidateIndexInCorrectAnswer = (candidate) => {
      const candidateWords = getNormalizedKeywordSequence(candidate);

      for (let index = 0; index <= correctWords.length - candidateWords.length; index += 1) {
        if (candidateWords.every((word, wordIndex) => correctWords[index + wordIndex] === word)) {
          return index;
        }
      }

      return Number.MAX_SAFE_INTEGER;
    };
    const orderedSelectedCandidates = [...selectedCandidates].sort((firstCandidate, secondCandidate) => (
      getCandidateIndexInCorrectAnswer(firstCandidate) - getCandidateIndexInCorrectAnswer(secondCandidate)
    ));

    return selectedCandidates.length >= 2
      && correctContainsAllCandidates
      && !wrongAnswerContainsAllCandidates
      && coveredWrongIndexes.size === wrongTexts.length
      ? orderedSelectedCandidates
      : [];
  };

  const getAutomaticKeywordSuggestion = (question) => {
    if (!question || String(question.questionType || '').toLowerCase() !== 'closed') {
      return '';
    }

    const correctAnswers = (question.answers || []).filter((answer) => answer.isCorrect);
    const wrongAnswers = (question.answers || []).filter((answer) => !answer.isCorrect);

    if (!correctAnswers.length || !wrongAnswers.length) {
      return '';
    }

    const correctText = correctAnswers.map((answer) => answer.text).join(' ');
    const wrongTexts = wrongAnswers.map((answer) => answer.text);
    const specialHint = getSpecialCorrectAnswerHint(correctText);

    if (specialHint) {
      return getKeywordLine(text.keywordDifference, [specialHint], 1);
    }

    if (!tokenizeKeywordText(correctText).length) {
      return '';
    }

    const visualCandidates = getBestKeywordCandidates(correctText, wrongTexts, 2, 'visual');

    if (visualCandidates.length) {
      return getKeywordLine(
        text.keywordDifference,
        visualCandidates.map((candidate) => candidate.phrase),
        2
      );
    }

    const mostSimilarWrongAnswer = getMostSimilarWrongAnswer(correctText, wrongAnswers);
    const differenceFromSimilarAnswer = (mostSimilarWrongAnswer?.differencePhrases || [])
      .map((phrase) => getDisplayKeywordTerm(correctText, phrase))
      .map((phrase) => cleanKeywordCandidate(phrase))
      .filter((phrase) => (
        phrase
        && !isWeakSingleKeyword(phrase)
        && getWrongPhrasePresenceCount(phrase, wrongTexts) === 0
      ));

    if (differenceFromSimilarAnswer.length) {
      return getKeywordLine(text.keywordDifference, differenceFromSimilarAnswer, 2);
    }

    const combinationCandidates = getKeywordCombinationCandidates(correctText, wrongTexts, 4);

    if (combinationCandidates.length) {
      return getKeywordLine(text.keywordDifference, combinationCandidates, 4);
    }

    const distinctiveCandidates = getBestKeywordCandidates(correctText, wrongTexts, 2, 'distinctive');

    if (distinctiveCandidates.length) {
      return getKeywordLine(
        text.keywordDifference,
        distinctiveCandidates.map((candidate) => candidate.phrase),
        2
      );
    }

    const fallbackDistinctivePhrases = getDistinctivePhrases(correctText, wrongTexts)
      .map((phrase) => cleanKeywordCandidate(phrase))
      .filter((phrase) => phrase && getWrongPhrasePresenceCount(phrase, wrongTexts) === 0);

    return fallbackDistinctivePhrases.length
      ? getKeywordLine(text.keywordDifference, fallbackDistinctivePhrases, 2)
      : '';
  };

  const getCurrentKeywordSuggestion = () => {
    const currentQuestionId = currentQuestionData?._id;
    const savedKeywordHint = sanitizeKeywordHint(keywordHintsByQuestionId[currentQuestionId] || '');

    if (savedKeywordHint) {
      return savedKeywordHint;
    }

    return keywordDisabledByQuestionId[currentQuestionId]
      ? ''
      : sanitizeKeywordHint(getAutomaticKeywordSuggestion(currentQuestionData));
  };

  const splitKeywordTerms = (value = '', { expandPhrases = false } = {}) => {
    const terms = value
      .split(/[,;\n]/)
      .map((term) => term.replace(/^[^:]+:\s*/, '').trim())
      .filter((term) => term.length > 2 && isReadableKeywordTerm(term));

    if (!expandPhrases) {
      return [...new Set(terms)];
    }

    const expandedTerms = terms.flatMap((term) => {
      const words = tokenizeKeywordText(term);
      return words.length > 1 ? [term, ...words] : [term];
    });

    return [...new Set(expandedTerms)];
  };

  const escapeKeywordRegex = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const getFlexibleKeywordRegexSource = (term = '') => {
    const keywordWords = getOriginalKeywordWords(term).map((word) => word.raw).filter(Boolean);
    const termSource = keywordWords.length > 1
      ? keywordWords.map(escapeKeywordRegex).join('[^\\p{L}\\p{N}]+')
      : escapeKeywordRegex(term.trim());

    return termSource ? `(?<![\\p{L}\\p{N}])${termSource}(?![\\p{L}\\p{N}])` : '';
  };

  const getFlexibleKeywordRegex = (term = '', flags = 'iu') => {
    const source = getFlexibleKeywordRegexSource(term);

    return source ? new RegExp(source, flags) : null;
  };

  const hasKeywordTermMatch = (textValue = '', term = '') => {
    const keywordRegex = getFlexibleKeywordRegex(term);

    return keywordRegex ? keywordRegex.test(textValue) : false;
  };

  const doKeywordTermsIdentifyCorrectAnswer = (terms = []) => {
    const readableTerms = terms.filter(isReadableKeywordTerm);

    if (readableTerms.length < 2 || !currentQuestionData?.answers?.length) {
      return false;
    }

    const answerContainsAllTerms = (answer) => readableTerms.every((term) => (
      hasKeywordTermMatch(String(answer.text || ''), term)
    ));
    const correctAnswers = currentQuestionData.answers.filter((answer) => answer.isCorrect);
    const wrongAnswers = currentQuestionData.answers.filter((answer) => !answer.isCorrect);

    return correctAnswers.some(answerContainsAllTerms) && !wrongAnswers.some(answerContainsAllTerms);
  };

  const getKeywordTermAnswerPresenceCount = (term = '') => (
    (currentQuestionData?.answers || []).filter((answer) => (
      hasKeywordTermMatch(String(answer.text || ''), term)
    )).length
  );

  const getKeywordHighlightTerms = () => {
    if (!isKeywordHelperEnabled || !showKeywordHighlights) {
      return [];
    }

    const currentHint = getCurrentKeywordSuggestion();

    if (!currentHint) {
      return [];
    }

    const lines = currentHint.split('\n');
    const rawContextLine = lines.find((line) => line.toLowerCase().startsWith(`${text.keywordContext.toLowerCase()}:`));
    const contextLine = rawContextLine && hasKeywordLineValue(rawContextLine) ? rawContextLine : '';
    const differenceLine = lines.find((line) => line.toLowerCase().startsWith(`${text.keywordDifference.toLowerCase()}:`));

    if (differenceLine) {
      const rawDifferenceTerms = dedupeOverlappingKeywordTerms(splitKeywordTerms(differenceLine));
      const isCombinationHint = doKeywordTermsIdentifyCorrectAnswer(rawDifferenceTerms);
      const differenceTerms = isCombinationHint
        ? rawDifferenceTerms
        : rawDifferenceTerms.filter((term) => getKeywordTermAnswerPresenceCount(term) <= 1);

      if (differenceTerms.length) {
        return differenceTerms.map((term) => ({ term, className: 'quiz-keyword-highlight-difference' }));
      }
    }

    if (contextLine) {
      return dedupeOverlappingKeywordTerms(splitKeywordTerms(contextLine))
        .filter((term) => getKeywordTermAnswerPresenceCount(term) <= 1)
        .map((term) => ({ term, className: 'quiz-keyword-highlight-context' }));
    }

    return dedupeOverlappingKeywordTerms(splitKeywordTerms(currentHint))
      .map((term) => ({ term, className: 'quiz-keyword-highlight-user' }));
  };

  const renderHighlightedAnswerText = (answerText = '') => {
    const keywordTerms = getKeywordHighlightTerms()
      .map((keywordTerm) => ({
        ...keywordTerm,
        source: getFlexibleKeywordRegexSource(keywordTerm.term),
      }))
      .filter(({ source, term }) => source && hasKeywordTermMatch(answerText, term))
      .sort((firstTerm, secondTerm) => secondTerm.term.length - firstTerm.term.length);

    if (!keywordTerms.length) {
      return answerText;
    }

    const keywordRegex = new RegExp(`(${keywordTerms.map(({ source }) => source).join('|')})`, 'giu');

    return answerText.split(keywordRegex).map((part, index) => {
      const matchedKeyword = keywordTerms.find(({ source }) => (
        new RegExp(`^${source}$`, 'iu').test(part)
      ));

      if (!matchedKeyword) {
        return part;
      }

      return (
        <mark key={`${part}-${index}`} className={`quiz-keyword-highlight ${matchedKeyword.className}`}>
          {part}
        </mark>
      );
    });
  };

  const goFullScreenIcon =    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-fullscreen" viewBox="0 0 16 16">
                              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5"/>
                            </svg>

  const exitFullScreenIcon =  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-fullscreen-exit" viewBox="0 0 16 16">
                                <path d="M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5m5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5M0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5m10 1a1.5 1.5 0 0 1 1.5-1.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0z"/>
                              </svg>
   // Funzione che attiva/disattiva la modalità fullscreen
   const toggleFullscreen = () => {
    const docEl = document.documentElement;
    if (!isFullscreen) {
      // Attiva modalità fullscreen
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen().catch(err => console.error("Error enabling full-screen:", err));
      } else if (docEl.mozRequestFullScreen) {
        docEl.mozRequestFullScreen().catch(err => console.error("Error enabling moz full-screen:", err));
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen().catch(err => console.error("Error enabling webkit full-screen:", err));
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen().catch(err => console.error("Error enabling ms full-screen:", err));
      }
    } else {
      // Uscita dalla modalità fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => console.error("Error exiting full-screen:", err));
      }
    }
  };

  // Controllare se siamo in modalità schermo intero
  const checkFullscreen = () => {
    setIsFullscreen(!!document.fullscreenElement); // Se c'è un elemento fullscreen, allora siamo in fullscreen
  };

  // Funzione che attiva la modalità fullscreen
  const goFullScreen = () => {
    const docEl = document.documentElement;
    if (docEl.requestFullscreen) {
      docEl.requestFullscreen().catch(err => {
        console.error("Error attempting to enable full-screen mode:", err);
      });
    } else if (docEl.mozRequestFullScreen) {
      docEl.mozRequestFullScreen().catch(err => {
        console.error("Error attempting to enable moz full-screen mode:", err);
      });
    } else if (docEl.webkitRequestFullscreen) {
      docEl.webkitRequestFullscreen().catch(err => {
        console.error("Error attempting to enable webkit full-screen mode:", err);
      });
    } else if (docEl.msRequestFullscreen) {
      docEl.msRequestFullscreen().catch(err => {
        console.error("Error attempting to enable ms full-screen mode:", err);
      });
    }
  };

  // Avvia il quiz in modalità fullscreen al caricamento del componente
  useEffect(() => {
    document.addEventListener("fullscreenchange", checkFullscreen);
    document.addEventListener("webkitfullscreenchange", checkFullscreen);
    document.addEventListener("mozfullscreenchange", checkFullscreen);
    document.addEventListener("MSFullscreenChange", checkFullscreen);

    if (!isSavedReview) {
      goFullScreen();
    }
    console.log("QuizScreen Questions: ", activeQuestions);
    // Gestione del timer
    const timer = !isSavedReview && timeRemaining != 0 ? (setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer);
          // Gestisci la fine del quiz quando il tempo finisce
          return null;
        }
        return prevTime - 1;
      });
    }, 1000)) : null;

    return () => {
      
      // Pulizia: uscita dalla modalità fullscreen e pulizia timer
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      }

      if (timer) clearInterval(timer);

      document.removeEventListener("fullscreenchange", checkFullscreen);
      document.removeEventListener("webkitfullscreenchange", checkFullscreen);
      document.removeEventListener("mozfullscreenchange", checkFullscreen);
      document.removeEventListener("MSFullscreenChange", checkFullscreen);
      }
      
  }, []);

  useEffect(() => {
    const questionId = activeQuestions[currentQuestion]?._id;

    if (questionId) {
      setViewedQuestionIds((previousViewedQuestionIds) => {
        const nextViewedQuestionIds = new Set(previousViewedQuestionIds);
        nextViewedQuestionIds.add(questionId);
        return nextViewedQuestionIds;
      });
    }
  }, [currentQuestion, activeQuestions]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const desktopMediaQuery = window.matchMedia('(min-width: 768px)');
    const closeMobileNavigatorOnDesktop = (event) => {
      if (event.matches) {
        setShowMobileQuestionNavigator(false);
      }
    };

    desktopMediaQuery.addEventListener?.('change', closeMobileNavigatorOnDesktop);

    return () => desktopMediaQuery.removeEventListener?.('change', closeMobileNavigatorOnDesktop);
  }, []);

  useEffect(() => {
    if (!isKeywordHelperEnabled) {
      setKeywordDraft('');
      setKeywordMessage('');
      return;
    }

    setKeywordDraft(getCurrentKeywordSuggestion());
    setKeywordMessage('');
    setShowKeywordPanel(false);
  }, [currentQuestion, isKeywordHelperEnabled]);

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < activeQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const getAnswerId = (question, answerIndex) => (
    question.answers?.[answerIndex]?._answerId || `${question._id}-answer-${answerIndex}`
  );

  const getCorrectAnswerIds = (question) => (question.answers || [])
    .map((answer, answerIndex) => (answer.isCorrect ? getAnswerId(question, answerIndex) : null))
    .filter(Boolean);

  const getSelectedAnswerIds = (questionIndex) => {
    const selectedAnswer = selectedAnswers[questionIndex];

    if (Array.isArray(selectedAnswer)) {
      return selectedAnswer;
    }

    return selectedAnswer ? [selectedAnswer] : [];
  };

  const isSameAnswerSet = (firstAnswers, secondAnswers) => (
    firstAnswers.length === secondAnswers.length
    && firstAnswers.every((answerId) => secondAnswers.includes(answerId))
  );

  const getQuestionResult = (questionIndex) => {
    const question = activeQuestions[questionIndex];

    if (!question || question.questionType === 'open') {
      return 'manual';
    }

    const selectedAnswerIds = getSelectedAnswerIds(questionIndex);

    if (!selectedAnswerIds.length) {
      return 'unanswered';
    }

    return isSameAnswerSet(selectedAnswerIds, getCorrectAnswerIds(question)) ? 'correct' : 'wrong';
  };

  const formatDuration = (durationSeconds) => {
    const safeDuration = Math.max(0, Number(durationSeconds || 0));
    const hours = Math.floor(safeDuration / 3600);
    const minutes = Math.floor((safeDuration % 3600) / 60);
    const seconds = safeDuration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    }

    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  };

  const buildCompletionSummary = (completedAt) => {
    const durationSeconds = Math.max(0, Math.round((completedAt.getTime() - startedAt.getTime()) / 1000));
    const closedQuestions = activeQuestions.filter((question) => question.questionType === 'closed');
    const openQuestions = activeQuestions.filter((question) => question.questionType === 'open');
    const correctClosed = activeQuestions.filter((question, index) => (
      question.questionType === 'closed' && getQuestionResult(index) === 'correct'
    )).length;
    const wrongClosed = activeQuestions.filter((question, index) => (
      question.questionType === 'closed' && ['wrong', 'unanswered'].includes(getQuestionResult(index))
    )).length;
    const scorePercent = closedQuestions.length ? Math.round((correctClosed / closedQuestions.length) * 100) : null;

    return {
      durationSeconds,
      closedTotal: closedQuestions.length,
      openTotal: openQuestions.length,
      correctClosed,
      wrongClosed,
      scorePercent,
      isOpenOnly: closedQuestions.length === 0 && openQuestions.length > 0,
    };
  };

  const getReferenceAnswerText = (question) => (
    question.answers?.find((answer) => answer.isCorrect)?.text
    || question.answers?.[0]?.text
    || text.noReferenceAnswer
  );

  const isAnswerFilled = (answer) => (
    Array.isArray(answer)
      ? answer.length > 0
      : answer !== null && answer !== undefined && answer !== ''
  );
  const requiresStudentAnswer = (question) => !(
    quizParams.autoShowOpenAnswers === true
    && String(question?.questionType || '').toLowerCase() === 'open'
  );

  const unansweredQuestionsCount = activeQuestions.filter((question, index) => (
    requiresStudentAnswer(question) && !isAnswerFilled(selectedAnswers[index])
  )).length;

  const handleUseSelectedKeywordText = () => {
    if (!isKeywordHelperEnabled) {
      return;
    }

    const selectedText = window.getSelection?.().toString().trim();

    if (selectedText) {
      setKeywordDraft(selectedText.slice(0, 500));
      setKeywordMessage('');
      window.getSelection?.().removeAllRanges?.();
    }
  };

  const handleSaveKeywordHint = async () => {
    if (!currentQuestionData?._id) {
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const nextKeywordHint = sanitizeKeywordHint(keywordDraft);

    try {
      setIsSavingKeyword(true);
      const response = await axios.put(`${apiUrl}/questionKeyword`, {
        questionId: currentQuestionData._id,
        keywordHint: nextKeywordHint,
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const savedKeywordHint = sanitizeKeywordHint(response.data.keywordHint || '');

      setKeywordHintsByQuestionId((previousHints) => ({
        ...previousHints,
        [currentQuestionData._id]: savedKeywordHint,
      }));
      setKeywordDisabledByQuestionId((previousDisabledHints) => ({
        ...previousDisabledHints,
        [currentQuestionData._id]: Boolean(response.data.keywordHintDisabled),
      }));
      setKeywordDraft(savedKeywordHint);
      setKeywordMessage(text.keywordSaved);
    } catch (error) {
      setKeywordMessage(error.response?.data?.message || text.keywordSaveError);
    } finally {
      setIsSavingKeyword(false);
    }
  };

  const getElapsedDurationSeconds = () => (
    Math.max(0, Math.round((new Date().getTime() - startedAt.getTime()) / 1000))
  );

  const getAttemptQuestionsPayload = () => activeQuestions.map((question, index) => {
    const selectedAnswer = selectedAnswers[index];

    return {
      questionId: question._id,
      wasViewed: viewedQuestionIds.has(question._id),
      wasAnswered: isAnswerValueFilled(selectedAnswer),
      selectedAnswer,
      answerOrder: (question.answers || []).map((answer, answerIndex) => getAnswerId(question, answerIndex)),
    };
  });

  useEffect(() => {
    if (!initialAttempt || isSavedReview) {
      return;
    }

    if (initialAttempt.currentQuestionIndex !== null && initialAttempt.currentQuestionIndex !== undefined) {
      return;
    }

    const firstUnansweredQuestionIndex = selectedAnswers.findIndex((answer, index) => (
      requiresStudentAnswer(activeQuestions[index]) && !isAnswerValueFilled(answer)
    ));

    if (firstUnansweredQuestionIndex >= 0) {
      setCurrentQuestion(firstUnansweredQuestionIndex);
    }
  }, []);

  useEffect(() => {
    if (isSavedReview || attemptId || hasStartedAttemptRequestRef.current) {
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      return;
    }

    hasStartedAttemptRequestRef.current = true;

    axios.post(`${apiUrl}/quizAttempts/start`, {
      subjectId: subject.isArchiveQuiz ? null : subject._id,
      archiveId: subject.archiveId || null,
      quizParams,
      startedAt: startedAt.toISOString(),
      questions: getAttemptQuestionsPayload(),
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    }).then((response) => {
      setAttemptId(response.data.attemptId);
    }).catch((error) => {
      hasStartedAttemptRequestRef.current = false;
      console.error('Error starting quiz attempt:', error);
      alert(text.validation.quizSaveError + ' ' + (error.response?.data?.message || error.message));
    });
  }, []);

  useEffect(() => {
    if (isSavedReview || isReviewMode || !attemptId || showCompletionModal) {
      return undefined;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      return undefined;
    }

    const syncTimer = setTimeout(() => {
      axios.put(`${apiUrl}/quizAttempts/${attemptId}/progress`, {
        durationSeconds: getElapsedDurationSeconds(),
        timeRemaining: timeRemaining === null ? null : timeRemaining,
        currentQuestionIndex: currentQuestion,
        questions: getAttemptQuestionsPayload(),
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      }).catch((error) => {
        console.error('Error saving quiz progress:', error);
      });
    }, 700);

    return () => clearTimeout(syncTimer);
  }, [attemptId, currentQuestion, selectedAnswers, viewedQuestionIds, timeRemaining, isReviewMode, showCompletionModal]);

  const handleRequestEndQuiz = () => {
    if (isSavedReview) {
      return;
    }

    setShowEndQuizConfirm(true);
  };

  const handleEndQuiz = async () => {
    if (isSavedReview) {
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const completedAt = new Date();
    const summary = buildCompletionSummary(completedAt);

    try {
      setIsSaving(true);
      await axios.post(`${apiUrl}/quizAttempts`, {
        attemptId,
        subjectId: subject.isArchiveQuiz ? null : subject._id,
        archiveId: subject.archiveId || null,
        quizParams,
        startedAt: startedAt.toISOString(),
        completedAt: completedAt.toISOString(),
        durationSeconds: summary.durationSeconds,
        questions: getAttemptQuestionsPayload(),
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      setCompletionSummary(summary);
      setShowEndQuizConfirm(false);
      setShowCompletionModal(true);
    } catch (error) {
      alert(text.validation.quizSaveError + ' ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviewQuiz = () => {
    setCurrentQuestion(0);
    setIsReviewMode(true);
    setShowCompletionModal(false);
  };

  const handleCloseQuizScreen = () => {
    setShowQuizScreen(false);
  };
  
  // Calcolo di ore, minuti e secondi
  const hours = Math.floor(timeRemaining / 3600).toString().padStart(2, '0');
  const minutes = Math.floor((timeRemaining % 3600) / 60).toString().padStart(2, '0');
  const seconds = (timeRemaining % 60).toString().padStart(2, '0');

  // Stato per le risposte selezionate (potrebbe essere un array di oggetti o ID)

  // Funzione per gestire il click sul numero della domanda
  const handleQuestionSelect = (index) => {
    setCurrentQuestion(index);
  };

  // Determina lo stile del pulsante in base alla domanda corrente e alle risposte
  const getButtonVariant = (questionIndex) => {
    if (isReviewMode) {
      const result = getQuestionResult(questionIndex);

      if (result === 'correct') {
        return 'success';
      }

      if (result === 'wrong') {
        return 'danger';
      }

      if (result === 'manual') {
        return 'warning';
      }

      return 'secondary';
    }

    if (currentQuestion === questionIndex) {
      return 'primary'; // Domanda corrente
    }
  
    const answerForQuestion = selectedAnswers[questionIndex];
  
    // Se è un array con risposte (checkbox) o ha una risposta selezionata (radio/textarea)
    if (Array.isArray(answerForQuestion) && answerForQuestion.length > 0) {
      return 'success'; // Domanda con risposte selezionate (checkbox)
    }
  
    if (answerForQuestion) {
      return 'success'; // Domanda con risposta selezionata (radio o textarea)
    }
  
    return 'secondary'; // Domanda senza risposta
  };

  const renderQuestionNavigator = (closeAfterSelection = false) => activeQuestions.map((_, index) => (
    <Button
      size="sm"
      key={index}
      className={`quiz-question-nav-button ${isReviewMode && currentQuestion === index ? 'quiz-review-question-current' : ''}`}
      variant={getButtonVariant(index)}
      aria-label={text.questionProgress(index + 1, activeQuestions.length)}
      aria-current={currentQuestion === index ? 'step' : undefined}
      onClick={() => {
        handleQuestionSelect(index);

        if (closeAfterSelection) {
          setShowMobileQuestionNavigator(false);
        }
      }}
    >
      {index + 1}
    </Button>
  ));

  const handleAnswerChange = (questionIndex, answerId) => {
    if (isReviewMode) {
      return;
    }

    setSelectedAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];
  
      if (activeQuestions[questionIndex].questionType === "open") {
        newAnswers[questionIndex] = answerId;
      }
      // Se è una domanda a risposta singola (radio)
      else if (activeQuestions[questionIndex].answerType === "single") {
        newAnswers[questionIndex] = answerId; // Imposta l'ID della risposta selezionata
      } 
      // Se è una domanda a risposta multipla (checkbox)
      else if (activeQuestions[questionIndex].answerType === "multiple") {
        const currentAnswers = newAnswers[questionIndex] || []; // Inizializza come array se non esiste
        if (currentAnswers.includes(answerId)) {
          // Deseleziona la risposta se è già selezionata
          const updatedAnswers = currentAnswers.filter(id => id !== answerId);
          newAnswers[questionIndex] = updatedAnswers.length > 0 ? updatedAnswers : undefined; // Rimuove se vuoto
        } else {
          // Seleziona la risposta
          newAnswers[questionIndex] = [...currentAnswers, answerId];
        }
      } 
      return newAnswers;
    });
  };

  const getAnswerCardProps = (answerId) => {
    if (isReviewMode) {
      return {};
    }

    return {
      role: 'button',
      tabIndex: 0,
      onClick: () => handleAnswerChange(currentQuestion, answerId),
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleAnswerChange(currentQuestion, answerId);
        }
      },
    };
  };
  
  return (
    questions && (
    <Fragment>
    <Modal className={`modal-theme-bg quiz-screen-modal ${isReviewMode ? 'quiz-review-mode' : ''}`} scrollable={true} show={true} fullscreen={fullscreen}>
      <Modal.Header className="quiz-screen-header d-flex flex-column align-items-center">
  <div className="d-flex justify-content-between w-100">
    <div>
      <h3 className="text-center">{text.quizSimulation}</h3>
      {currentQuestionArchiveName && <small className="text-muted ms-1 d-block">{currentQuestionArchiveName}</small>}
      {currentQuestionSubjectSubtitle && <small className="text-muted ms-1 d-block">{currentQuestionSubjectSubtitle}</small>}
    </div>
    
    {/* Icona per il fullscreen */}
    <span onClick={toggleFullscreen} className="fullscreen-icon me-3">
      {isFullscreen ? exitFullScreenIcon : goFullScreenIcon}
    </span>
  </div>

  {/* Aggiungi una riga per centrare i pulsanti di navigazione */}
  <div className="quiz-question-navigation-desktop d-none d-md-flex justify-content-center">
    <div className="question-navigation d-flex flex-wrap justify-content-center">
      {renderQuestionNavigator()}
    </div>
  </div>
  <Button
    type="button"
    variant="soft-primary"
    className="quiz-mobile-navigation-trigger d-md-none"
    aria-haspopup="dialog"
    aria-expanded={showMobileQuestionNavigator}
    onClick={() => setShowMobileQuestionNavigator(true)}
  >
    <span>{text.questions}</span>
    <strong>{currentQuestion + 1} / {activeQuestions.length}</strong>
  </Button>
</Modal.Header>
      <Modal.Body className="quiz-screen-body">
        <div className="quiz-screen">
          <Row className="mb-3">
            <Col>
              <Card className="bg-soft-black rounded-3">
                <Card.Body>
                  {/* Visualizza qui la domanda e le risposte in base al tipo */}
                  <Row>
                    <Col className='mb-3'>
                      <div className="quiz-question-progress d-flex align-items-center gap-2 ms-3 pe-5">
                        <h3 className="text-muted mb-0">{text.questionProgress(currentQuestion + 1, activeQuestions.length)}</h3>
                        {isKeywordHelperEnabled && (
                          <Button
                            type="button"
                            variant={showKeywordPanel ? 'primary' : 'soft-info'}
                            size="sm"
                            className="quiz-keyword-toggle"
                            title={text.findKeyword}
                            onClick={() => setShowKeywordPanel((isVisible) => !isVisible)}
                          >
                            💡
                          </Button>
                        )}
                      </div>
                      {/* Badge per il numero della domanda */}
                      <div className="position-absolute top-0 end-0 mt-3 me-3  me-md-4">
                        <Badge pill bg="primary" className="p-2 fs-6">{currentQuestion + 1}</Badge>
                      </div>
                    </Col>
                  </Row>
                  {isKeywordHelperEnabled && showKeywordPanel && (
                    <Card className="bg-dark mb-4 rounded-3 quiz-keyword-card">
                      <Card.Body>
                        <Form.Group>
                          <div className="quiz-keyword-header d-flex align-items-center gap-2 mb-2">
                            <Form.Label className="mb-0">💡 {text.keywordLabel}</Form.Label>
                            <OverlayTrigger
                              trigger="click"
                              placement="top"
                              rootClose
                              overlay={(
                                <Popover id="keyword-help-popover" className="quiz-keyword-popover">
                                  <Popover.Body>
                                    <div className="fw-semibold mb-2">{text.keywordTooltipTitle}</div>
                                    <div className="mb-2">
                                      <div className="fw-semibold">{text.keywordContext}</div>
                                      <div>{text.keywordTooltipContext}</div>
                                    </div>
                                    <div className="mb-2">
                                      <div className="fw-semibold">{text.keywordDifference}</div>
                                      <div>{text.keywordTooltipDifference}</div>
                                    </div>
                                    <div className="quiz-keyword-tooltip-format">
                                      {text.keywordTooltipFormat}
                                    </div>
                                  </Popover.Body>
                                </Popover>
                              )}
                            >
                              <span className="quiz-keyword-info" tabIndex={0}>i</span>
                            </OverlayTrigger>
                            <Form.Check
                              className="quiz-keyword-highlight-switch ms-sm-auto"
                              type="switch"
                              id="quiz-keyword-highlight-switch"
                              label={text.keywordHighlightToggle}
                              checked={showKeywordHighlights}
                              onChange={(event) => setShowKeywordHighlights(event.target.checked)}
                            />
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={keywordDraft}
                            onChange={(event) => {
                              setKeywordDraft(event.target.value);
                              setKeywordMessage('');
                            }}
                            placeholder={
                              !keywordDisabledByQuestionId[currentQuestionData?._id]
                              && getAutomaticKeywordSuggestion(currentQuestionData)
                                ? text.keywordPlaceholder
                                : ''
                            }
                          />
                        </Form.Group>
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 mt-3">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={handleSaveKeywordHint}
                            disabled={isSavingKeyword}
                          >
                            {isSavingKeyword
                              ? text.saving
                              : keywordHintsByQuestionId[currentQuestionData?._id]
                                ? text.updateKeyword
                                : text.saveKeyword}
                          </Button>
                          <Button
                            type="button"
                            variant="soft-info"
                            size="sm"
                            onClick={() => {
                              const nextSuggestion = getAutomaticKeywordSuggestion(currentQuestionData);

                              setKeywordDisabledByQuestionId((previousDisabledHints) => ({
                                ...previousDisabledHints,
                                [currentQuestionData._id]: false,
                              }));
                              setKeywordDraft(nextSuggestion);
                              setKeywordMessage(nextSuggestion ? '' : text.noKeywordFound);
                            }}
                          >
                            {text.regenerateKeyword}
                          </Button>
                          {keywordMessage && <small className="text-muted">{keywordMessage}</small>}
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                  <Row className="mb-3">
                    <Col>
                      <Card className="bg-dark rounded-3">
                        <Card.Body>
                          <Card.Title as="h3" className="">{activeQuestions[currentQuestion].questionText}</Card.Title>
                          {activeQuestions[currentQuestion].passageText && (
                            <Button
                              type="button"
                              variant="soft-info"
                              size="sm"
                              className="mt-3"
                              onClick={() => setSelectedPassage(activeQuestions[currentQuestion])}
                            >
                              {text.readPassage(activeQuestions[currentQuestion].passageNumber)}
                            </Button>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  {/* Se la domanda è di tipo "text", mostra solo la textarea */}
                  {activeQuestions[currentQuestion].questionType === "open" ? (
                    <Col sm="12" key={activeQuestions[currentQuestion]._id}>
                      <Card className="bg-dark mb-4 rounded-3">
                        <Card.Body>
                          {isReviewMode && (
                            <Badge bg="warning" text="dark" className="mb-3">{text.manualReviewBadge}</Badge>
                          )}
                          {isAutomaticOpenAnswer ? (
                            <div className="quiz-review-box">
                              <div className="fw-semibold mb-1">{text.automaticReferenceAnswer}</div>
                              <p className="mb-0 text-break">{getReferenceAnswerText(activeQuestions[currentQuestion])}</p>
                            </div>
                          ) : (
                            <Form.Group>
                              <Form.Label className='ms-1'>{text.enterYourAnswer}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={selectedAnswers[currentQuestion] || ''}
                                onChange={(e) => handleAnswerChange(currentQuestion, e.target.value)}
                                placeholder={text.openAnswerPlaceholder}
                                disabled={isReviewMode}
                              />
                            </Form.Group>
                          )}
                          {isReviewMode && (
                            <div className="quiz-review-box mt-3">
                              <div className="fw-semibold mb-1">{text.yourAnswer}</div>
                              <p className="mb-3">{selectedAnswers[currentQuestion] || text.noAnswerGiven}</p>
                              <div className="fw-semibold mb-1">{text.referenceAnswer}</div>
                              <p className="mb-0">{getReferenceAnswerText(activeQuestions[currentQuestion])}</p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ) : (
                    <Fragment>
                    <Row className="quiz-answers-grid row-cols-1 row-cols-md-2 row-cols-lg-4 mb-3 text-center">
                      {activeQuestions[currentQuestion].answers.map((answer, answerIndex) => {
                        const answerId = getAnswerId(activeQuestions[currentQuestion], answerIndex);
                        const isRadioChecked = selectedAnswers[currentQuestion] === answerId; // Per radio
                        const isCheckboxChecked = selectedAnswers[currentQuestion]?.includes(answerId); // Per checkbox
                        const selectedAnswerIds = getSelectedAnswerIds(currentQuestion);
                        const isSelectedAnswer = selectedAnswerIds.includes(answerId);
                        const answerReviewClass = isReviewMode && answer.isCorrect
                          ? 'quiz-answer-correct'
                          : isReviewMode && isSelectedAnswer && !answer.isCorrect
                            ? 'quiz-answer-wrong'
                            : '';

                        return activeQuestions[currentQuestion].answerType === "single" && activeQuestions[currentQuestion].questionType === "closed" ? (
                          <Col md="12" key={answerId}>
                            <Card className={`bg-dark mb-4 rounded-3 quiz-answer-card ${answerReviewClass}`} {...getAnswerCardProps(answerId)}>
                              <Card.Body>
                                <Form.Check key={answerId} className="form-check-inline" onClick={(event) => event.stopPropagation()} onMouseUp={answer.isCorrect ? handleUseSelectedKeywordText : undefined}>
                                  <FormCheck.Input
                                    type="radio"
                                    id={answerId}
                                    checked={isRadioChecked}
                                    onChange={(event) => {
                                      event.stopPropagation();
                                      handleAnswerChange(currentQuestion, answerId);
                                    }} // Gestisci il cambiamento
                                    disabled={isReviewMode}
                                  />
                                  <FormCheck.Label htmlFor={answerId} className="pl-2">
                                    {renderHighlightedAnswerText(answer.text)}
                                  </FormCheck.Label>
                                </Form.Check>
                                <hr />
                              </Card.Body>
                            </Card>
                          </Col>
                        ) : activeQuestions[currentQuestion].answerType === "multiple" ? (
                          <Col md="12" key={answerId}>
                            <Card className={`bg-dark mb-4 rounded-3 quiz-answer-card ${answerReviewClass}`} {...getAnswerCardProps(answerId)}>
                              <Card.Body>
                                <Form.Check key={answerId} className="form-check-inline" onClick={(event) => event.stopPropagation()} onMouseUp={answer.isCorrect ? handleUseSelectedKeywordText : undefined}>
                                  <FormCheck.Input
                                    type="checkbox"
                                    id={answerId}
                                    checked={isCheckboxChecked} // Controlla se la checkbox è selezionata
                                    onChange={(event) => {
                                      event.stopPropagation();
                                      handleAnswerChange(currentQuestion, answerId);
                                    }} // Gestisci il cambiamento
                                    disabled={isReviewMode}
                                  />
                                  <FormCheck.Label htmlFor={answerId} className="pl-2">
                                    {renderHighlightedAnswerText(answer.text)}
                                  </FormCheck.Label>
                                </Form.Check>
                                <hr />
                              </Card.Body>
                            </Card>
                          </Col>
                        ) : null; // Gestisce altri tipi di domande
                      })}
                      {isReviewMode && (activeQuestions[currentQuestion].explanation || activeQuestions[currentQuestion].description) && (
                        <Col md="12">
                          <div className="quiz-review-box text-start">
                            <div className="fw-semibold mb-1">{text.explanation}</div>
                            <p className="mb-0">
                              {activeQuestions[currentQuestion].explanation || activeQuestions[currentQuestion].description}
                            </p>
                          </div>
                        </Col>
                      )}
                    </Row>
                    </Fragment>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        {/* <Button className=" me-2 mb-2" variant="soft-success" size="sm" onClick={handleShowAddQuestion}>
              {add2} Question & Answers
          </Button>
          <QuestionsTable selectedSubject={selectedSubject} questions={questions} setQuestions={setQuestions} />*/}
      </Modal.Body>
      <Modal.Footer className="quiz-screen-footer d-flex justify-content-between align-items-center">
        {/* Timer a sinistra */}
        <div className="timer ms-3">
          {isReviewMode && completionSummary ? (
            text.timeSummary(formatDuration(completionSummary.durationSeconds))
          ) : timeRemaining !== null && timeRemaining !== 0 ? (
            text.timeLeft + " " + (hours > 0 ? hours + ":" : "") + minutes + ":" + seconds
          ) : ("")}
        </div>
        {/* Bottoni a destra */}
        <div className="quiz-screen-actions d-flex">
          <Button
            className="me-2 mb-2"
            variant={currentQuestion > 0 ? "soft-success" : "outline-success"}
            size=""
            onClick={handlePrevQuestion}
            disabled={currentQuestion === 0}
          >
            {text.previous}
          </Button>

          <Button
            className="me-2 mb-2"
            variant={currentQuestion < activeQuestions.length - 1 ? "soft-success" : "outline-success"}
            size=""
            onClick={handleNextQuestion}
            disabled={currentQuestion >= activeQuestions.length - 1}
          >
            {text.next}
          </Button>

        {isReviewMode ? (
          <>
            <Button className="me-2 mb-2" variant="primary" size="" onClick={() => setShowCompletionModal(true)}>
              {text.result}
            </Button>
            <Button className="me-2 mb-2" variant="danger" size="" onClick={handleCloseQuizScreen}>
              {text.close}
            </Button>
          </>
        ) : (
          <Button className="me-2 mb-2" variant="danger" size="" onClick={handleRequestEndQuiz} disabled={isSaving}>
            {isSaving ? text.saving : text.endQuiz}
          </Button>
        )}
      </div>
      </Modal.Footer>
    </Modal>
    <Modal
      className="modal-theme-bg quiz-question-navigator-modal"
      dialogClassName="quiz-question-navigator-dialog"
      show={showMobileQuestionNavigator}
      onHide={() => setShowMobileQuestionNavigator(false)}
      aria-labelledby="quiz-question-navigator-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="quiz-question-navigator-title" as="h5">
          {text.questions} · {currentQuestion + 1} / {activeQuestions.length}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="quiz-mobile-question-grid">
          {renderQuestionNavigator(true)}
        </div>
      </Modal.Body>
    </Modal>
    <Modal
      className="modal-theme-bg quiz-end-confirm-modal"
      show={showEndQuizConfirm}
      onHide={() => !isSaving && setShowEndQuizConfirm(false)}
      centered
      backdrop="static"
      keyboard={!isSaving}
    >
      <Modal.Header>
        <Modal.Title as="h5">{text.endQuizConfirmTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">{text.endQuizConfirmText}</p>
        {unansweredQuestionsCount > 0 && (
          <p className="mt-3 mb-0 text-warning fw-semibold">
            {text.unansweredQuestionsWarning(unansweredQuestionsCount)}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer className="quiz-completion-actions">
        <Button type="button" variant="soft-secondary" onClick={() => setShowEndQuizConfirm(false)} disabled={isSaving}>
          {text.goBack}
        </Button>
        <Button type="button" variant="danger" onClick={handleEndQuiz} disabled={isSaving}>
          {isSaving ? text.saving : text.confirmEndQuiz}
        </Button>
      </Modal.Footer>
    </Modal>
    <QuizResultModal
      completionSummary={completionSummary}
      formatDuration={formatDuration}
      onClose={handleCloseQuizScreen}
      onReview={handleReviewQuiz}
      show={showCompletionModal}
      text={text}
    />
    {selectedPassage && (
      <Modal className="modal-theme-bg" show={!!selectedPassage} onHide={() => setSelectedPassage(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{text.passageTitle(selectedPassage.passageNumber)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{selectedPassage.passageText}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="soft-danger" onClick={() => setSelectedPassage(null)}>{text.close}</Button>
        </Modal.Footer>
      </Modal>
    )}
    </Fragment>
    )
  );
};

export default QuizScreen;
